import React, { useState } from 'react';
    import { Button } from './Button';
    import { CheckCircle, Loader2, ArrowRight, AlertCircle } from 'lucide-react';
    
    interface ContactProps {
      selectedPackage?: string;
    }
    
    export const Contact: React.FC<ContactProps> = ({ selectedPackage }) => {
      const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        website: '',
        interest: 'content',
      });
    
      const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    
      const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
      };
    
      const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        
        try {
            const response = await fetch('https://n8n-op.bemany.world/webhook/leads/zackig', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    package: selectedPackage || undefined
                }),
            });

            if (response.ok) {
                setStatus('success');
            } else {
                setStatus('error');
            }
        } catch (error) {
            console.error('Submission failed', error);
            setStatus('error');
        }
      };
    
      return (
        <section id="contact" className="py-24 bg-brand-orange relative overflow-hidden">
           {/* Background Patterns */}
           <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
    
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              
              <div className="lg:w-1/2 text-white">
                <h2 className="text-6xl md:text-8xl font-black mb-8 uppercase leading-[0.85] tracking-tighter">
                  Lass uns <br/>zackig <br/>machen.
                </h2>
                <p className="text-xl font-bold mb-12 opacity-90 max-w-md">
                  Füll das Formular aus. Wir melden uns mit einem Angebot & Strategie für dein Wachstum.
                </p>
                
                <div className="flex items-center gap-6 bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/20">
                    <div className="w-14 h-14 rounded-full bg-white text-brand-orange flex items-center justify-center font-bold text-2xl shadow-lg">
                        <ArrowRight strokeWidth={3} />
                    </div>
                    <div>
                        <div className="font-black text-xl uppercase">Direkter Draht</div>
                        <div className="text-sm opacity-80">zu den Gründern.</div>
                    </div>
                </div>
              </div>
    
              <div className="lg:w-1/2 w-full">
                <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border-4 border-black/5">
                  
                  {status === 'success' ? (
                    <div className="text-center py-16 animate-fade-in-up">
                      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <CheckCircle className="w-12 h-12 text-green-600" />
                      </div>
                      <h3 className="text-4xl font-black text-brand-dark mb-4 uppercase">Erledigt!</h3>
                      <p className="text-lg text-gray-600 mb-8 font-medium">
                        Wir haben deine Anfrage erhalten. Check deine Mails.
                      </p>
                      <Button variant="outline" onClick={() => setStatus('idle')}>
                        Neue Anfrage
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <h3 className="text-3xl font-black text-brand-dark mb-6 uppercase">Projekt Starten</h3>
                      
                      {status === 'error' && (
                        <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3 font-bold text-sm">
                            <AlertCircle size={20} />
                            Es gab einen Fehler. Bitte versuche es erneut.
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wide mb-2 text-gray-400">Name</label>
                          <input 
                            type="text" 
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full p-4 bg-gray-50 border-2 border-gray-100 focus:border-brand-orange rounded-xl font-bold text-brand-dark focus:outline-none transition-colors"
                            required
                            placeholder="Dein Name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wide mb-2 text-gray-400">Firma</label>
                          <input 
                            type="text" 
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full p-4 bg-gray-50 border-2 border-gray-100 focus:border-brand-orange rounded-xl font-bold text-brand-dark focus:outline-none transition-colors"
                            required
                            placeholder="Company GmbH"
                          />
                        </div>
                      </div>
    
                      <div>
                          <label className="block text-xs font-bold uppercase tracking-wide mb-2 text-gray-400">E-Mail</label>
                          <input 
                            type="email" 
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-4 bg-gray-50 border-2 border-gray-100 focus:border-brand-orange rounded-xl font-bold text-brand-dark focus:outline-none transition-colors"
                            required
                            placeholder="du@company.com"
                          />
                      </div>
    
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wide mb-2 text-gray-400">Website</label>
                            <input 
                                type="text" 
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                className="w-full p-4 bg-gray-50 border-2 border-gray-100 focus:border-brand-orange rounded-xl font-bold text-brand-dark focus:outline-none transition-colors"
                                placeholder="www..."
                            />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wide mb-2 text-gray-400">Interesse</label>
                          <select 
                            name="interest"
                            value={formData.interest}
                            onChange={handleChange}
                            className="w-full p-4 bg-gray-50 border-2 border-gray-100 focus:border-brand-orange rounded-xl font-bold text-brand-dark focus:outline-none transition-colors appearance-none"
                          >
                            <option value="content">Content Production</option>
                            <option value="web">Website / Funnels</option>
                            <option value="full">Full Service</option>
                          </select>
                        </div>
                      </div>
    
                      <Button 
                        type="submit" 
                        fullWidth 
                        variant="dark" 
                        className="!py-5 !text-xl mt-4 shadow-xl uppercase tracking-wider hover:scale-[1.02]"
                        disabled={status === 'submitting'}
                      >
                        {status === 'submitting' ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          'Angebot anfordern'
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    };